import { Inject, Injectable, Renderer2 } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { ButtonNamesGA, GoogleIdsMapping, ModalNamesGA, PageNamesGA, PathNamesGA } from '@cpak/consts';
import {
  map,
  filter,
  startWith,
  distinctUntilChanged,
  shareReplay,
  first,
  Observable,
  take,
  combineLatest,
} from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ModeNamesMappingGA, WindowWithGoogleAnalytics } from '@cpak/consts';
import { DOCUMENT } from '@angular/common';
import { WINDOW } from '@cpak/shared/injectors';
import { GoogleIds, PakBomViewMode, PakLocationViewMode } from '@cpak/enums';
import { StartService } from './start/start.service';
import { ConfigData } from '@cpak/adp-cpak-bff-service-models';
import { CountriesTerritoriesFacade } from '@cpak/main-features-lib/data-store';

type ClickMetadata = {
  buttonName: typeof ButtonNamesGA[keyof typeof ButtonNamesGA];
  modalName?: typeof ModalNamesGA[keyof typeof ModalNamesGA];
  pageName?: string;
  tabName?: string;
  modeName?: PakBomViewMode | PakLocationViewMode;
};

@UntilDestroy()
@Injectable()
export class TrackingService {
  private readonly currentUrl$ = this.router.events.pipe(
    filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    map(event => event.urlAfterRedirects),
    startWith(this.router.url),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private readonly pageName$ = this.currentUrl$.pipe(
    map(this.getPageNameFromUrl),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public startConfig$ = this.startService.getStartResponse().pipe(
    map(startResponse => startResponse?.data.config),
    filter((config): config is ConfigData => !!config),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly googleAnalyticsTrackingId$: Observable<typeof GoogleIds[keyof typeof GoogleIds]> =
    this.startConfig$.pipe(
      map(startConfig => {
        const plant = startConfig.defaultPlant;

        const environment = startConfig.environment;

        const trackingId = GoogleIdsMapping[plant][environment];

        return trackingId;
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

  public constructor(
    private readonly router: Router,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(WINDOW) private readonly window: WindowWithGoogleAnalytics,
    private readonly startService: StartService,
    private readonly countriesTerritoriesFacade: CountriesTerritoriesFacade
  ) {}

  public trackClick(metadata: ClickMetadata): void {
    const shouldIncludePageName = !metadata.modalName;
    const modeName = metadata.modeName ? ModeNamesMappingGA.get(metadata.modeName) : undefined;

    combineLatest([this.pageName$.pipe(first()), this.countriesTerritoriesFacade.getSelectedCountries()])
      .pipe(take(1))
      .subscribe(([pageName, selectedCountryId]) => {
        const event = {
          type: 'click',
          button_name: metadata.buttonName,
          modal_name: metadata.modalName,
          page_name: shouldIncludePageName ? pageName : undefined,
          tab_name: metadata.tabName,
          mode_name: modeName,
          country_id: selectedCountryId,
        };
        if (this.window.gtag) {
          this.window.gtag('event', 'click', event);
        }
      });
  }

  public initTracking(renderer: Renderer2, isProduction: boolean, userRoles: string[]): void {
    // page navigations are automatically tracked by gtag
    this.currentUrl$.pipe(untilDestroyed(this)).subscribe();

    this.googleAnalyticsTrackingId$.pipe(first()).subscribe(trackingId => {
      const addGtagScript = renderer.createElement('script');
      addGtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
      addGtagScript.async = true;
      renderer.appendChild(this.document.head, addGtagScript);

      this.window.dataLayer = this.window.dataLayer || [];
      const dataLayer = this.window.dataLayer;
      this.window.gtag = function () {
        dataLayer.push(arguments);
      };

      this.window.gtag('js', new Date());

      const userRolesObj = userRoles.reduce((acc, role) => {
        acc[role] = true;
        return acc;
      }, {} as { [key: string]: boolean });

      this.window.gtag('config', trackingId, {
        debug_mode: !isProduction,
        ...userRolesObj,
      });
    });
  }

  private getPageNameFromUrl(url: string): typeof PageNamesGA[keyof typeof PageNamesGA] | undefined {
    try {
      const parsedUrl = new URL(url, this.window.location.origin);

      const path = parsedUrl.pathname;

      const segments = path
        .split('/')
        .reverse()
        .filter(segment => !!segment);

      const matchingSegment = segments.find(segment => PathNamesGA.has(segment));

      if (!matchingSegment) {
        return undefined;
      }

      return PathNamesGA.get(matchingSegment) as typeof PageNamesGA[keyof typeof PageNamesGA];
    } catch (e) {
      return undefined;
    }
  }
}
